// Filter bar — every control mutates the single `filters` object owned by the
// page; nothing here refetches (filters are applied client-side in
// safetyFilters.js). Option lists for manner/light/surface derive from the
// loaded crash data so they always match reality.
//
// Layout: a PRIMARY row that is always visible (analysis, direction, milepost
// and the three view toggles) and an "Advanced filters" panel, collapsed by
// default, grouped into Crash data / Mechanism / Countermeasures.
import React, { useState } from 'react';
import { distinctValues } from './safetyFilters';
import { CMF_POLICIES } from './cmfBenefit';
import { MultiPicker } from './CorridorPicker';

const ANALYSES = [
  { key: 'SSD', label: 'SSD — stopping sight' },
  { key: 'PSD', label: 'PSD — passing sight' },
  { key: 'GLR', label: 'GLR — sun glare' },
  { key: 'FSA', label: 'FSA — flat spot analysis' },
  { key: 'HCA', label: 'HCA — horizontal curves' },
  { key: 'GRW', label: 'GRW — guardrail warrant' },
  { key: 'ISD', label: 'ISD — intersections' },
];
const SEVERITIES = [
  { key: 'fatal', label: 'Fatal' },
  { key: 'injury', label: 'Injury' },
  { key: 'pdo', label: 'PDO' },
  { key: 'unknown', label: 'Unknown' },
];

export const RESET_FILTERS_PATCH = {
  analyses: [], mpRange: null, years: [], severities: [], manners: [], lights: [], surfaces: [],
  excludeAnimal: false, wetOnly: false, glareOnly: false, embankmentOnly: false,
  passingOnly: false, aboveChanceOnly: false, correlatedOnly: false, showCrashes: true, dateRange: null, direction: 'both',
  showCmf: true, cmfPolicy: '45', discountRate: 7,
};

function MultiSelect({ label, options, selected, onChange }) {
  return (
    <label className="safety-filter">
      <span>{label}</span>
      <select
        multiple
        size={Math.min(4, Math.max(2, options.length))}
        value={selected}
        onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))}
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}

/** Count of advanced filters that differ from their defaults (badge). */
function activeAdvancedCount(f) {
  let n = 0;
  if (f.years.length) n += 1;
  if (f.severities.length) n += 1;
  if (f.manners.length) n += 1;
  if (f.lights.length) n += 1;
  if (f.surfaces.length) n += 1;
  if (f.excludeAnimal || f.wetOnly || f.glareOnly || f.embankmentOnly || f.passingOnly) n += 1;
  if (f.aboveChanceOnly) n += 1;
  if (f.cmfPolicy !== '45' || Number(f.discountRate) !== 7) n += 1;
  return n;
}

export default function FilterBar({ filters, setFilters, crashes, corridorExtent, datasetYears, singleCorridor = true }) {
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [mpMin, mpMax] = corridorExtent || [null, null];
  const range = filters.mpRange || corridorExtent || [0, 0];
  const activeCount = activeAdvancedCount(filters);

  return (
    <div className="safety-filters">
      {/* ---- primary row: always visible ---- */}
      <div className="safety-filterbar safety-filterbar-primary">
        <MultiPicker label="Analyses" options={ANALYSES} selected={filters.analyses || []}
          onChange={(v) => set({ analyses: v })} noun="analyses" allMeansEmpty
          title="One analysis shows its metric strip and severity colours; several (or all) merge their runs, coloured by analysis" />

        <label className="safety-filter">
          <span>Direction</span>
          <select value={filters.direction} onChange={(e) => set({ direction: e.target.value })}>
            <option value="both">Both</option>
            <option value="P">Eastbound / P</option>
            <option value="N">Westbound / N</option>
          </select>
        </label>

        {mpMin !== null && singleCorridor ? (
          <label className="safety-filter safety-filter-range">
            <span>Milepost {range[0].toFixed(1)} – {range[1].toFixed(1)}</span>
            <span className="safety-range-pair">
              <input type="range" min={mpMin} max={mpMax} step={0.1} value={range[0]}
                onChange={(e) => set({ mpRange: [Math.min(+e.target.value, range[1]), range[1]] })} />
              <input type="range" min={mpMin} max={mpMax} step={0.1} value={range[1]}
                onChange={(e) => set({ mpRange: [range[0], Math.max(+e.target.value, range[0])] })} />
            </span>
          </label>
        ) : (
          <div className="safety-filter safety-filter-range">
            <span>Milepost</span>
            <span className="safety-muted safety-filter-hint">
              {singleCorridor ? 'loading…' : 'full length of every selected corridor'}
            </span>
          </div>
        )}

        <div className="safety-filter safety-filter-toggles">
          <span>View</span>
          <div className="safety-toggle-row">
            <button
              type="button"
              className={`safety-toggle ${filters.correlatedOnly ? 'on' : ''}`}
              onClick={() => set({ correlatedOnly: !filters.correlatedOnly })}
              title="Keep only crashes with an RDV deficiency finding at their location (the Insights set), and the runs near them"
            >
              {filters.correlatedOnly ? '✓ ' : ''}Correlated only
            </button>
            <button
              type="button"
              className={`safety-toggle ${filters.showCrashes ? '' : 'on'}`}
              onClick={() => set({ showCrashes: !filters.showCrashes })}
              title="Hide the crash layers and tables to review the RDV analyses on their own"
            >
              {filters.showCrashes ? 'Hide crash data' : '✓ Analyses only'}
            </button>
            <button
              type="button"
              className={`safety-toggle ${filters.showCmf ? 'on' : ''}`}
              onClick={() => set({ showCmf: !filters.showCmf })}
              title="Show the recommended countermeasure, its FHWA crash modification factor and the expected benefit on every deficiency site"
            >
              {filters.showCmf ? '✓ ' : ''}Countermeasures &amp; benefit
            </button>
          </div>
        </div>

        <button type="button"
          className={`safety-advanced-toggle ${advancedOpen ? 'open' : ''}`}
          onClick={() => setAdvancedOpen(!advancedOpen)}
          aria-expanded={advancedOpen}>
          <span className="safety-section-chevron">{advancedOpen ? '▾' : '▸'}</span>
          Advanced filters
          {activeCount > 0 && <span className="safety-badge" title="active advanced filters">{activeCount}</span>}
        </button>
      </div>

      {/* ---- advanced panel: collapsed by default ---- */}
      {advancedOpen && (
        <div className="safety-advanced">
          <fieldset className="safety-subpanel">
            <legend>Crash data</legend>
            <div className="safety-subpanel-body">
              <div className="safety-filter">
                <span>Years</span>
                <div className="safety-checks">
                  {/* every year the DATASET holds — a year with zero corridor crashes
                      shows "(0)" instead of silently disappearing */}
                  {(() => {
                    const counts = {};
                    (crashes || []).forEach((c) => {
                      const y = String(c.year);
                      counts[y] = (counts[y] || 0) + 1;
                    });
                    const all = [...new Set([...(datasetYears || []).map(String),
                      ...distinctValues(crashes, 'year')])].sort((a, b) => a - b);
                    return all.map((y) => (
                      <label key={y} className={counts[y] ? '' : 'safety-year-empty'}
                        title={counts[y] ? undefined : 'No crashes on the selected corridors for this year'}>
                        <input
                          type="checkbox"
                          checked={!filters.years.length || filters.years.includes(y)}
                          onChange={(e) => {
                            const current = filters.years.length ? filters.years : all;
                            const next = e.target.checked
                              ? [...new Set([...current, y])]
                              : current.filter((k) => k !== y);
                            set({ years: next.length === all.length ? [] : next });
                          }}
                        />
                        {y} ({counts[y] || 0})
                      </label>
                    ));
                  })()}
                </div>
              </div>

              <div className="safety-filter">
                <span>Severity</span>
                <div className="safety-checks">
                  {SEVERITIES.map((s) => (
                    <label key={s.key}>
                      <input
                        type="checkbox"
                        checked={!filters.severities.length || filters.severities.includes(s.key)}
                        onChange={(e) => {
                          const current = filters.severities.length
                            ? filters.severities
                            : SEVERITIES.map((x) => x.key);
                          const next = e.target.checked
                            ? [...new Set([...current, s.key])]
                            : current.filter((k) => k !== s.key);
                          set({ severities: next.length === SEVERITIES.length ? [] : next });
                        }}
                      />
                      {s.label}
                    </label>
                  ))}
                </div>
              </div>

              <MultiSelect label="Manner" options={distinctValues(crashes, 'manner')}
                selected={filters.manners} onChange={(v) => set({ manners: v })} />
              <MultiSelect label="Light" options={distinctValues(crashes, 'light')}
                selected={filters.lights} onChange={(v) => set({ lights: v })} />
              <MultiSelect label="Surface" options={distinctValues(crashes, 'surface')}
                selected={filters.surfaces} onChange={(v) => set({ surfaces: v })} />
            </div>
          </fieldset>

          <fieldset className="safety-subpanel">
            <legend>Filters</legend>
            <div className="safety-subpanel-body">
              <div className="safety-checks safety-checks-col">
                <label>
                  <input type="checkbox" checked={filters.excludeAnimal}
                    onChange={(e) => set({ excludeAnimal: e.target.checked })} />
                  Exclude animal strikes
                </label>
                <label title="FSA: flat cross slope drains poorly — wet / ice / snow surface crashes">
                  <input type="checkbox" checked={filters.wetOnly}
                    onChange={(e) => set({ wetOnly: e.target.checked })} />
                  Wet / snow surface only
                </label>
                <label title="GLR: crash date & time falls inside the location's glare impact ranges (direction-matched)">
                  <input type="checkbox" checked={filters.glareOnly}
                    onChange={(e) => set({ glareOnly: e.target.checked })} />
                  Glare-matched only
                </label>
                <label title="GRW: vehicle went over the embankment / overturned">
                  <input type="checkbox" checked={filters.embankmentOnly}
                    onChange={(e) => set({ embankmentOnly: e.target.checked })} />
                  Embankment / overturn only
                </label>
                <label title="PSD: head-on or opposite-direction sideswipe — the crash types a failed passing manoeuvre produces">
                  <input type="checkbox" checked={filters.passingOnly}
                    onChange={(e) => set({ passingOnly: e.target.checked })} />
                  Head-on / opposite-direction only
                </label>
                <label title="Keep only the deficiency runs of analyses whose Statistical-support verdict is Above chance on that corridor, and the crashes correlated with them">
                  <input type="checkbox" checked={!!filters.aboveChanceOnly}
                    onChange={(e) => set({ aboveChanceOnly: e.target.checked })} />
                  Above-chance verdicts only
                </label>
              </div>
            </div>
          </fieldset>

          <fieldset className="safety-subpanel">
            <legend>Countermeasures</legend>
            <div className="safety-subpanel-body">
              <label className="safety-filter"
                title="Evidence policy for crash modification factors (FHWA CMF Clearinghouse star quality rating). Default: 4–5 stars; a 3-star CMF is used only for analyses with no higher-rated study (SSD crest curves, ISD sight triangles).">
                <span>CMF quality</span>
                <select value={filters.cmfPolicy} onChange={(e) => set({ cmfPolicy: e.target.value })}>
                  {CMF_POLICIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </label>
              <label className="safety-filter" title="Real discount rate for the present value of the service-life benefit (7% = federal OMB convention)">
                <span>Discount rate %</span>
                <input type="number" className="safety-num-input" min={0} max={15} step={0.5}
                  value={filters.discountRate}
                  onChange={(e) => set({ discountRate: e.target.value === '' ? 0 : Math.max(0, +e.target.value) })} />
              </label>
            </div>
          </fieldset>

          <div className="safety-advanced-foot">
            <button type="button" className="safety-btn safety-btn-ghost"
              onClick={() => setFilters((f) => ({ ...f, ...RESET_FILTERS_PATCH }))}>
              Reset all filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
